import React, { useState, useEffect, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { setLoading, setSnackInfo } from '../../redux/actions/appAction';
import HttpService from '../../services/httpService';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TablePagination from '@material-ui/core/TablePagination';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Autocomplete from '@material-ui/lab/Autocomplete';
import GetAppIcon from '@material-ui/icons/GetApp';
import RefreshIcon from '@material-ui/icons/Refresh';

const useStyles = makeStyles(() => ({
  root: { padding: '20px' },
  headerRow: { marginBottom: 16 },
  title: { margin: 0, color: '#1C375D', fontWeight: 700 },
  subNote: { color: '#888', fontSize: 13, marginTop: 4 },
  filterBar: {
    display: 'flex',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    gap: 16,
    padding: '16px',
    marginBottom: 16,
  },
  dateField: { display: 'flex', flexDirection: 'column' },
  dateLabel: { fontSize: 11, fontWeight: 700, color: '#888', letterSpacing: 0.5, marginBottom: 4 },
  itemsField: { flex: 1, minWidth: 260 },
  loadingContainer: { display: 'flex', justifyContent: 'center', padding: '40px' },
  inward: { color: '#2e7d32', fontWeight: 700 },
  outward: { color: '#c62828', fontWeight: 700 },
}));

const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const StyledTableCell = withStyles(() => ({
  head: {
    backgroundColor: '#1C375D',
    color: '#fff',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  body: { fontSize: 14 },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
  },
}))(TableRow);

// Default date range is the current calendar month so far.
const firstDayOfCurrentMonth = () => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
};
const today = () => new Date().toISOString().slice(0, 10);

const StockMovementLog = (props) => {
  const classes = useStyles();
  const { setLoading: setGlobalLoading, setSnackInfo } = props;

  // Raw (editable) date inputs vs the ones actually applied to the last fetch — filters here are
  // "Apply Filters"-driven, not live-as-you-type, so a fetch only fires on Apply (same convention
  // as the Stock Movement dashboard).
  const [fromDate, setFromDate] = useState(firstDayOfCurrentMonth());
  const [toDate, setToDate] = useState(today());
  const [appliedFromDate, setAppliedFromDate] = useState(firstDayOfCurrentMonth());
  const [appliedToDate, setAppliedToDate] = useState(today());

  const [itemInput, setItemInput] = useState('');
  const [itemOptions, setItemOptions] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]); // [{_id, name}]
  const [appliedItems, setAppliedItems] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchSeq = useRef(0);

  // Item search typeahead — server-side, debounced. Reuses the same items endpoint the Stock
  // Movement dashboard's filter uses.
  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const response = await HttpService.getStockMovementItems(itemInput);
        setItemOptions(response?.data?.data || []);
      } catch (error) {
        console.error('Error searching items:', error);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [itemInput]);

  const buildFilterParams = useCallback(() => ({
    startDate: appliedFromDate,
    endDate: appliedToDate,
    ...(appliedItems.length ? { itemIds: appliedItems.map(i => i._id).join(',') } : {}),
  }), [appliedFromDate, appliedToDate, appliedItems]);

  const fetchLogs = useCallback(async () => {
    const seq = ++fetchSeq.current;
    setLoading(true);
    try {
      const response = await HttpService.getStockMovementLogs({
        ...buildFilterParams(),
        page: page + 1,
        limit: rowsPerPage,
      });
      if (fetchSeq.current !== seq) return; // a newer request already superseded this one
      if (response?.data?.success) {
        setRows(response.data.data || []);
        setTotalCount(response.data.pagination?.totalCount || 0);
      }
    } catch (error) {
      console.error('Error fetching stock movement logs:', error);
      setSnackInfo?.("Couldn't load Stock Movement Logs", 'error');
    } finally {
      if (fetchSeq.current === seq) setLoading(false);
    }
  }, [buildFilterParams, page, rowsPerPage, setSnackInfo]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const applyFilters = () => {
    setAppliedFromDate(fromDate);
    setAppliedToDate(toDate);
    setAppliedItems(selectedItems);
    setPage(0);
  };

  const resetFilters = () => {
    setFromDate(firstDayOfCurrentMonth());
    setToDate(today());
    setSelectedItems([]);
    setItemInput('');
    setAppliedFromDate(firstDayOfCurrentMonth());
    setAppliedToDate(today());
    setAppliedItems([]);
    setPage(0);
  };

  const handleDownloadExcel = async () => {
    setGlobalLoading(true);
    try {
      const response = await HttpService.downloadStockMovementLogsExcel(buildFilterParams());
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `stock-movement-logs_${appliedFromDate}_to_${appliedToDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading stock movement logs Excel:', error);
      setSnackInfo?.("Couldn't prepare Excel export", 'error');
    } finally {
      setGlobalLoading(false);
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.headerRow}>
        <h2 className={classes.title}>Stock Movement Logs</h2>
        <div className={classes.subNote}>{totalCount} log(s)</div>
      </div>

      <Paper className={classes.filterBar}>
        <div className={classes.dateField}>
          <span className={classes.dateLabel}>FROM</span>
          <TextField type="date" size="small" variant="outlined" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <span style={{ marginBottom: 8 }}>→</span>
        <div className={classes.dateField}>
          <span className={classes.dateLabel}>TO</span>
          <TextField type="date" size="small" variant="outlined" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>
        <div className={classes.itemsField}>
          <span className={classes.dateLabel}>ITEMS (multi-select)</span>
          <Autocomplete
            multiple
            size="small"
            value={selectedItems}
            onChange={(e, newValue) => setSelectedItems(newValue || [])}
            onInputChange={(e, newInput) => setItemInput(newInput)}
            options={itemOptions}
            getOptionLabel={(option) => option?.name || ''}
            getOptionSelected={(option, value) => option._id === value._id}
            renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Search & select items..." />}
          />
        </div>
        <Button variant="contained" style={{ background: '#1C375D', color: '#fff' }} onClick={applyFilters}>Apply Filters</Button>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={resetFilters}>Reset</Button>
        <Button variant="outlined" startIcon={<GetAppIcon />} onClick={handleDownloadExcel}>Download Excel</Button>
      </Paper>

      {loading ? (
        <div className={classes.loadingContainer}><CircularProgress /></div>
      ) : (
        <TableContainer component={Paper}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <StyledTableCell align="center">SR NO.</StyledTableCell>
                <StyledTableCell>ITEM NAME</StyledTableCell>
                <StyledTableCell>DEAL ACTIVITY</StyledTableCell>
                <StyledTableCell>ORDER ID</StyledTableCell>
                <StyledTableCell align="center">STOCK MOVEMENT TYPE</StyledTableCell>
                <StyledTableCell align="center">QTY</StyledTableCell>
                <StyledTableCell>CREATE DATE</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" style={{ padding: 30, color: '#888' }}>
                    No logs found for the selected filters
                  </TableCell>
                </TableRow>
              ) : rows.map((row, idx) => {
                const isOutward = row.movementType === 'Outward';
                const qtyClass = row.movementType ? (isOutward ? classes.outward : classes.inward) : undefined;
                return (
                  <StyledTableRow key={row._id}>
                    <TableCell align="center">{page * rowsPerPage + idx + 1}</TableCell>
                    <TableCell>{row.itemName}</TableCell>
                    <TableCell>{row.dealActivity}</TableCell>
                    <TableCell>{row.orderId}</TableCell>
                    <TableCell align="center" className={qtyClass}>{row.movementType}</TableCell>
                    <TableCell align="center" className={qtyClass}>
                      {row.quantity != null ? `${isOutward ? '-' : '+'}${fmt(row.quantity)}` : ''}
                    </TableCell>
                    <TableCell>{row.createDate ? new Date(row.createDate).toLocaleString() : ''}</TableCell>
                  </StyledTableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[25, 50, 100, { label: 'All', value: totalCount || 1 }]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          />
        </TableContainer>
      )}
    </div>
  );
};

export default connect(null, { setLoading, setSnackInfo })(StockMovementLog);
